<template>
  <div class="p-grid">
    <div class="p-col-12">
      <div class="card">
        <BillingFeatureGate feature="warehouse"
                            variant="page"
                            :title="$t('Stock categories')"
                            :description="$t('billing.features.warehouse.tooltip')"
                            icon="ti-package">

          <Toolbar class="p-mb-4">
            <template #start>
              <div style="font-size: 1.4em; color: inherit" class="p-mr-3">{{ $t('Stock categories') }}</div>
              <AddNewButton v-show="canModerate" :label="$t('Add')" @click="addNewItem"/>
            </template>
          </Toolbar>

          <Spinner v-if="isLoading"/>
          <DesktopDataTable v-else
                            :showHeader="false"
                            :dataTableIsLoading="dataTableIsLoading"
                            :itemsArrayIsEmpty="itemsArrayIsEmpty"
                            :sort="sort"
                            :showSearchInput="false"
                            :actionButtonsCount="actionButtonsCount"
                            @sort-items="sortItems"
                            :headers="[
                            {name: 'name', title: $t('Name'),sortable: true},
                            // {name: 'description', title: 'Description', sortable: true},
                            // {name: 'all_items_count', title: 'All items count', width: '200px', sortable: false},
                            {name: 'active_items_count', title: $t('Count'), width: '20%', sortable: false},
                            {name: 'created_at', title: $t('Created'), width: '20%', sortable: true },
                          ]">
            <template v-slot:body>
              <tr v-for="(item, index) of items"
                  :key="index"
                  :class="{'updated-item': isItemUpdated(item.id)}">
                <td :class="{'bold-text':item.isUncategorized}">
                  <span v-if="item.isUncategorized">{{ $t('Uncategorized') }}</span>
                  <template v-else>
                    <span v-if="item.name">{{ item.name }}</span>
                    <i v-else class="ti-minus"/>
                  </template>
                </td>

                <!--              !!!!Dont delete-->
                <!--              <td :class="{'bold-text':item.isUncategorized}">-->
                <!--                <span v-if="item.allItemsCount">{{ item.allItemsCount }}</span>-->
                <!--                <i v-else class="ti-minus"/>-->
                <!--              </td>-->

                <td :class="{'bold-text':item.isUncategorized}">
                <span v-if="item.activeItemsCount !== null && item.activeItemsCount !== undefined">
                  {{ item.activeItemsCount }}
                </span>
                  <i v-else class="ti-minus"/>
                </td>
                <td class="table-date-line">
                  <span v-if="item.created_at">{{ formatBy(item.created_at, item.createdBy) }}</span>
                  <i v-else class="ti-minus"/>
                </td>
                <td v-show="actionButtonsCount">
                  <div class="actions" v-if="!item.isUncategorized">
                    <EditButton v-show="canModerate"
                                :disabled="dataTableIsLoading" @click="editItem(item)" />
                    <DeleteButton v-show="canModerate"
                                  @click="confirmItemDelete(item)"
                                  :disabled="dataTableIsLoading"/>
                  </div>
                </td>
              </tr>
            </template>
          </DesktopDataTable>

          <OwnPaginator v-show="items?.length"
                        :rows="pagination.perPage"
                        :currentPage="pagination.currentPage - 1"
                        :totalPages="pagination.pageCount"
                        :totalRecords="pagination.totalCount"
                        :rowsPerPageOptions="[5,10,25,50,100,200]"
                        :pageLinkSize="11"/>

          <TotalRecordsData :pagination="pagination" :itemsArrayIsEmpty="itemsArrayIsEmpty"/>

          <WarehouseCategoryModal :visible="itemModal"
                                  :item="item"
                                  @update-items="updateItems"
                                  @close="closeItemModal"/>

          <ConfirmDeleteModal :visible="confirmItemDeleteModal"
                              :modalDataIsLoading="modalDataIsLoading"
                              @close="closeConfirmItemDeleteModal"
                              @delete-item="deleteItem"
                              :data="itemToDeleteData"/>
        </BillingFeatureGate>
      </div>
    </div>
  </div>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from "@/mixins/formatMixins";
import datatableMixins from "@/mixins/datatableMixins";
import settings from "@/settings";
import sortingMixins from "@/mixins/sortingMixins";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import WarehouseCategoryModal from "@/pages/data_directory/components/WarehouseCategoryModal";
import BillingFeatureGate from "@/components/billing/BillingFeatureGate.vue";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'

export default {
  mixins: [ formatMixins, datatableMixins, showErrorsMixins, sortingMixins, billingFeaturesMixins ],
  components: { WarehouseCategoryModal, BillingFeatureGate },
  data() {
    return {
      fieldsExpand: 'createdBy,allItemsCount,activeItemsCount',
      apiLink: 'warehouse/get-categories',
      deleteItemUrl: 'warehouse/delete-category',
      routePath: '/stock-categories',
      sort: {
        column: 'name',
        direction: '',
      },
    }
  },
  watch: {
    canUseWarehouse(allowed, wasAllowed) {
      if (allowed && !wasAllowed) {
        this.$nextTick(() => {
          this.checkRouteQuery()
          this.getPageData()
        })
        return
      }

      if (!allowed && wasAllowed) {
        this.resetWarehouseCategoriesState()
      }
    },
  },
  mounted() {
    this.scrollToTop()
    if (settings.autoLogoutTimeout && this.$store.state.logoutTimestampIsOverdue) return false
    // this.checkRouteQuery()
    // if (this.canUseWarehouse) this.getPageData()
    if (this.canUseWarehouse) {
      this.checkRouteQuery()
      this.getPageData()
    }
  },
  methods: {
    getPageData() {
      if (!this.canUseWarehouse) return
      this.getItems()
    },
    resetWarehouseCategoriesState() {
      this._abortors?.warehouseCategories?.abort?.()
      this.items = []
      this.isLoading = false
      this.dataTableIsLoading = false

      this.itemModal = false
      this.confirmItemDeleteModal = false
    },
    async getItems() {
      if (!this.canUseWarehouse || !this.userCanGetData || this.fetchInFlight) return
      this.$store.commit('SET_FETCH_IN_FLIGHT', true)
      this.dataTableIsLoading = true
      const ctrl = this.setController?.('warehouseCategories')
      try {
        const { status, data, headers } = await httpClient({
          url: this.apiLink,
          params: {
            page: this.pagination.currentPage,
            'per-page': this.pagination.perPage,
            sort: this.sort.direction + this.sort.column,
            search: this.processedSearchValue ?? null,
            expand: this.fieldsExpand,
          },
          signal: ctrl?.signal
        })
        if (!this.canUseWarehouse) return
        if (status === 200 && data) {
          const hasPaginationHeaders = !!headers['x-pagination-total-count']
          if (hasPaginationHeaders) {
            this.pagination.totalCount = +headers['x-pagination-total-count']
            this.pagination.pageCount  = +headers['x-pagination-page-count']
          } else {
            this.pagination.totalCount = data.length
            this.pagination.pageCount  = 1
          }
          // const items = data.categories && Array.isArray(data.categories) ? data.categories : []
          //
          // this.items = items
          //
          // const uncategorizedAllCount = data.uncategorized_all_count ? +data.uncategorized_all_count : 0
          // const uncategorizedActiveCount = data.uncategorized_active_count ? +data.uncategorized_active_count : 0
          //
          // if (uncategorizedAllCount || uncategorizedActiveCount) {
          //   this.items.push({
          //     isUncategorized: true,
          //     allItemsCount: uncategorizedAllCount,
          //     activeItemsCount: uncategorizedActiveCount
          //   })
          // }
          //
          // if (!items.length) {
          //   this.pagination.pageCount = 1
          //   this.pagination.currentPage = 1
          // }

          const categories = Array.isArray(data.categories) ? data.categories : []

          this.items = [...categories]

          const uncategorizedAllCount = Number(data.uncategorized_all_count || 0)
          const uncategorizedActiveCount = Number(data.uncategorized_active_count || 0)

          if (uncategorizedAllCount || uncategorizedActiveCount) {
            this.items.push({
              isUncategorized: true,
              allItemsCount: uncategorizedAllCount,
              activeItemsCount: uncategorizedActiveCount
            })
          }

          if (!categories.length) {
            this.pagination.pageCount = 1
            this.pagination.currentPage = 1
          }
        } else {
          this.items = []
          this.showUnknownError()
        }
      } catch(err) {
        if (!this.canUseWarehouse) return
        this.showError(err)
      } finally {
        this.$store.commit('SET_FETCH_IN_FLIGHT', false)
        this.dataTableIsLoading = false
        this.isLoading = false
        if (this._abortors?.warehouseCategories === ctrl) {
          this._abortors.warehouseCategories = null
        }
      }
    },
  },
  computed: {
    canModerate() {
      return this.$store.getters.isAccountantOrHigher
    },
    actionButtonsCount() {
      return this.canModerate ? 2 : 0
    },
    itemToDeleteData() {
      if (!this.itemToDelete?.name) return null
      return this.itemToDelete.name
    },
  }
}
</script>

<style scoped lang="scss">
td {
  height: 44px
}
</style>
